// src/components/BuilderErrorBoundary.tsx
// Specialized error boundary for the builder workspace
import * as React from "react";
import { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { safeError } from "@/lib/safeError";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, MessageSquare } from "lucide-react";

interface Props {
  children: ReactNode;
  projectId?: string;
  projectName?: string;
}

interface State {
  hasError: boolean;
  errorMessage: string;
  errorId: string | null;
}

export class BuilderErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: "", errorId: null };
  }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    const info = safeError(error);
    return { hasError: true, errorMessage: info.message };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    const info = safeError(error);
    console.error("[BuilderErrorBoundary]", info.name, info.message);

    // Send to Sentry with builder context
    Sentry.withScope((scope) => {
      scope.setTag("errorType", "builder_crash");
      scope.setTag("component", "BuilderWorkspace");
      if (this.props.projectId) {
        scope.setExtra("projectId", this.props.projectId);
      }
      if (this.props.projectName) {
        scope.setExtra("projectName", this.props.projectName);
      }
      scope.setExtra("componentStack", errorInfo.componentStack);

      const eventId = Sentry.captureException(info.raw);
      this.setState({ errorId: eventId });
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, errorMessage: "", errorId: null });
  };

  handleReportFeedback = () => {
    if (this.state.errorId) {
      Sentry.showReportDialog({ eventId: this.state.errorId });
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex-1 flex items-center justify-center bg-[#080810] p-8">
          <div className="max-w-lg w-full text-center space-y-6">
            <div className="mx-auto w-20 h-20 rounded-2xl bg-amber-500/10 flex items-center justify-center">
              <AlertTriangle className="h-10 w-10 text-amber-500" />
            </div>

            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-white">
                Builder encountered an error
              </h2>
              <p className="text-gray-400">
                {this.state.errorMessage || "Something went wrong while building your app."}
              </p>
              {this.props.projectName && (
                <p className="text-sm text-gray-500">
                  Project: {this.props.projectName}
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={this.handleReset}
                variant="outline"
                className="border-gray-700 hover:bg-gray-800"
              >
                Try Again
              </Button>
              <Button
                onClick={this.handleReload}
                className="gap-2 bg-indigo-600 hover:bg-indigo-700"
              >
                <RefreshCw className="h-4 w-4" />
                Reload Builder
              </Button>
              {this.state.errorId && (
                <Button
                  onClick={this.handleReportFeedback}
                  variant="ghost"
                  className="gap-2 text-gray-400 hover:text-white"
                >
                  <MessageSquare className="h-4 w-4" />
                  Report Issue
                </Button>
              )}
            </div>

            <p className="text-xs text-gray-600">
              Error ID: {this.state.errorId || "unknown"}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default BuilderErrorBoundary;
